import React from "react";
import { Box, Typography, TextField, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import { Tables } from "../../../database.types";
import { App } from "../../types/types";
import { StandardButton } from "../../components/StandardButton";
import GameListItem from "./GameListItem";

interface GamesTabContentProps {
  variant: "my-games" | "league-games";
  games: App.GameWithCreator[];
  teams: App.TeamWithMembers[];
  teamsMap: Record<string, Tables<"teams">>;
  onCopyGameLink: (game: App.GameWithCreator) => Promise<void>;
  onDeleteGame: (game: App.GameWithCreator) => Promise<void>;
  onStartGame: (game: App.GameWithCreator) => void;
  newGameName?: string;
  setNewGameName?: (name: string) => void;
  onCreateNewGame?: () => Promise<void>;
}

export const GamesTabContent: React.FC<GamesTabContentProps> = ({
  variant,
  games,
  teams,
  teamsMap,
  onCopyGameLink,
  onDeleteGame,
  onStartGame,
  newGameName,
  setNewGameName,
  onCreateNewGame,
}) => {
  const theme = useTheme();
  const isMyGames = variant === "my-games";

  return (
    <>
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        overflow="hidden"
        sx={{ pt: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 } }}
      >
        <Typography color="white" alignSelf="center" gutterBottom>
          {isMyGames
            ? "Games that you created and a winner has not been declared"
            : "All Completed gamess"}
        </Typography>

        {!isMyGames && games.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2 }}
          >
            No completed league games yet.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: games.length > 0 ? 1 : undefined,
              gap: 2,
              overflow: games.length > 0 ? "auto" : "visible",
            }}
          >
            {games.map((game) => {
              const teamOne =
                game.team_one_id !== null
                  ? teamsMap[game.team_one_id]
                  : null;
              const teamTwo =
                game.team_two_id !== null
                  ? teamsMap[game.team_two_id]
                  : null;
              const winningTeamName = game.winning_team_id
                ? teams.find((t) => t.id === game.winning_team_id)?.name
                : undefined;

              return (
                <GameListItem
                  key={game.id}
                  game={game}
                  teamOne={teamOne}
                  teamTwo={teamTwo}
                  winningTeamName={winningTeamName}
                  onCopyLink={() => onCopyGameLink(game)}
                  onDelete={() => onDeleteGame(game)}
                  onStart={() => onStartGame(game)}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {isMyGames && setNewGameName && onCreateNewGame && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 2,
            flexShrink: 0,
            minWidth: 200,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: theme.palette.primary.dark,
            borderRadius: 2,
          }}
        >
          {games.length === 0 && (
            <React.Fragment>
              <Box sx={{ mb: 2 }}>
                <SportsEsportsIcon
                  sx={{ fontSize: 48, color: "primary.main", mb: 1 }}
                />
                <Typography variant="h5" gutterBottom fontWeight="600">
                  No games found
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Get started by creating your first game.
                </Typography>
              </Box>
            </React.Fragment>
          )}

          <TextField
            label="Game name"
            value={newGameName}
            onChange={(e) => setNewGameName(e.target.value)}
            size="medium"
            fullWidth
            inputProps={{ "data-testid": "new-game-name-input" }}
            sx={{
              mb: 2,
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiInputLabel-root": { color: "white" },
              "& .MuiInputLabel-root.Mui-focused": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "white",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                { borderColor: "white" },
            }}
          />

          <StandardButton
            data-testid="create-new-game-button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onCreateNewGame}
            disabled={!newGameName}
            size="medium"
            fullWidth
            sx={{
              color: "white",
              borderColor: "white",
              ":disabled": { color: "gray", borderColor: "gray" },
            }}
          >
            Create New Game
          </StandardButton>
        </Box>
      )}
    </>
  );
};
